import { Menu, Transition } from "@headlessui/react";
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  MagnifyingGlassIcon,
  MoonIcon,
  ShieldCheckIcon,
  SunIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useTenants } from "../tenants/hooks/useTenants";

import { logout } from "@/auth/authSlice";
import { Button } from "@/components/Button";
import type { RootState } from "@/redux/store";
import { getDeviceIdentifier, logout as logoutApi } from "@/services/authService";
import { toggleTheme } from "@/theme/themeSlice";

const MAX_RESULTS = 6;

/**
 * Global search over the tenant list the console has already loaded. There is no platform-wide
 * search endpoint, so rather than ship a box that queries nothing, this narrows the one dataset
 * that genuinely exists and jumps straight to a tenant.
 */
function TenantQuickSearch() {
  const navigate = useNavigate();
  const { data: tenants } = useTenants();
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];

    return (tenants ?? [])
      .filter(
        (tenant) =>
          tenant.name.toLowerCase().includes(needle) ||
          tenant.code.toLowerCase().includes(needle) ||
          tenant.email.toLowerCase().includes(needle),
      )
      .slice(0, MAX_RESULTS);
  }, [tenants, query]);

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const select = (tenantId: string) => {
    setQuery("");
    setIsOpen(false);
    navigate(`/platform/tenants/${tenantId}`);
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <MagnifyingGlassIcon
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
        aria-hidden="true"
      />
      <input
        type="search"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setIsOpen(false);
          if (event.key === "Enter" && matches[0]) select(matches[0].id);
        }}
        placeholder="Search tenants by name, code, or email"
        aria-label="Search tenants"
        className="h-10 w-full rounded-md border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:bg-slate-900"
      />

      {isOpen && query.trim() !== "" && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
          {matches.length === 0 ? (
            <p className="px-3 py-3 text-sm text-slate-500 dark:text-slate-400">
              No tenants match that search.
            </p>
          ) : (
            <ul>
              {matches.map((tenant) => (
                <li key={tenant.id}>
                  <button
                    type="button"
                    onClick={() => select(tenant.id)}
                    className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <span className="text-sm font-medium text-slate-900 dark:text-white">
                      {tenant.name}
                    </span>
                    <span className="caption">
                      {tenant.code} &middot; {tenant.email}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

interface PlatformHeaderProps {
  onSidebarToggle: () => void;
  onMobileMenuToggle: () => void;
}

export function PlatformHeader({
  onSidebarToggle,
  onMobileMenuToggle,
}: PlatformHeaderProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const theme = useSelector((state: RootState) => state.theme?.theme ?? "light");

  const handleSignOut = async () => {
    await logoutApi(getDeviceIdentifier());
    dispatch(logout());
    navigate("/login");
  };

  return (
    <header className="sticky top-[var(--platform-banner-h,0px)] z-30 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
      <div className="flex items-center gap-3 px-4 py-3 sm:px-6">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onMobileMenuToggle}
          aria-label="Open navigation menu"
          className="rounded-md p-2 lg:hidden"
        >
          <Bars3Icon className="h-5 w-5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onSidebarToggle}
          aria-label="Toggle sidebar"
          className="hidden rounded-md p-2 lg:inline-flex"
        >
          <Bars3Icon className="h-5 w-5" />
        </Button>

        <div className="flex min-w-0 items-center gap-2">
          <ShieldCheckIcon
            className="hidden h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400 sm:block"
            aria-hidden="true"
          />
          <h1 className="truncate text-sm font-semibold text-slate-900 dark:text-white">
            Platform Owner Console
          </h1>
        </div>

        <div className="ml-auto flex flex-1 items-center justify-end gap-2">
          <div className="hidden flex-1 justify-end md:flex">
            <TenantQuickSearch />
          </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => dispatch(toggleTheme())}
            aria-label="Toggle theme"
            className="rounded-md p-2"
          >
            {theme === "light" ? (
              <MoonIcon className="h-5 w-5" />
            ) : (
              <SunIcon className="h-5 w-5" />
            )}
          </Button>

          <Menu as="div" className="relative inline-block text-left">
            <Menu.Button className="flex items-center gap-2 rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200">
              <UserCircleIcon className="h-8 w-8" />
              <span className="hidden text-left md:block">
                <span className="block text-sm font-medium text-slate-900 dark:text-white">
                  {user?.name ?? "Platform Owner"}
                </span>
                <span className="caption block">Platform Owner</span>
              </span>
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-slate-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:divide-slate-800 dark:bg-slate-900">
                <div className="px-3 py-2">
                  <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                    {user?.name ?? "Platform Owner"}
                  </p>
                  <p className="caption truncate">Platform scope</p>
                </div>
                <div className="px-1 py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={handleSignOut}
                        className={clsx(
                          "w-full !justify-start rounded-md px-2 py-2 text-sm",
                          active
                            ? "bg-rose-500 text-white hover:bg-rose-500 hover:text-white"
                            : "text-slate-900 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800",
                        )}
                      >
                        <ArrowRightOnRectangleIcon className="mr-2 h-5 w-5" /> Sign out
                      </Button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>

      <div className="border-t border-slate-200 px-4 py-2 dark:border-slate-800 md:hidden">
        <TenantQuickSearch />
      </div>
    </header>
  );
}
